import { useDisclosure } from "@msuite/ui/hooks";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { ReturnOrderListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { EditReturnOrderVehiclesModal } from "@/modals/stock/edit-return-order-vehicles-modal";

/**
 * What the office does to a return run from the list: open it, change which lorries are fetching
 * it, and call it off.
 *
 * Editing means the fleet and nothing else. A run's site and note are what its printed sheet
 * already says, and there is no route that would change them — so the menu offers the one thing
 * the office actually revises after filing.
 *
 * Completing is deliberately not here. A run is signed off at the gate once the last pallet is
 * counted, by whoever counted it, and the detail page and the handheld both carry that action
 * beside the figures it depends on.
 */
export function RowActions({ cell }: CellContext<ReturnOrderListItem, "actions">) {
	/** Hooks */
	const { t } = useTranslation();
	const editVehiclesModal = useDisclosure();
	const cancelModal = useAsyncModal();

	/** Context */
	const { refetch } = useDataTableContext();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	// The same three-state guard `apps/web/src/components/citadel/can.tsx` documents: dropped only
	// on a definitive denial, because while the rules load the server keeps the last word. Both
	// entries write, and both routes gate on `update Stock` — attaching a truck is read as a change
	// to the run it hangs on rather than as a creation of its own.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");
	const isClosed = ["complete", "canceled"].includes(cell.row.original.state);

	/** Functions */
	// Confirmed, because nothing is reversed by it: whatever the site already handed back is
	// physically on the shelf and stays booked, and the run cannot be reopened.
	async function handleOnCancel() {
		const isConfirmed = await cancelModal.confirm();
		if (!isConfirmed) return;
		const response = await client.stock["return-orders"][":returnOrderId"].state.$patch({
			param: { returnOrderId: cell.row.original.id },
			json: { state: "canceled" },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={`/organization/data/stock/return-orders/${cell.row.original.id}`}>
				{!stockUpdateIsDenied && !isClosed && (
					<Menu
						items={createStandardMenuItems({
							onEdit: editVehiclesModal.onOpen,
							onEditOptions: {
								label: t("stock.return_edit_vehicles"),
								icon: icon("Truck"),
							},
							onDelete: handleOnCancel,
							onDeleteOptions: {
								label: t("stock.return_cancel_order"),
								icon: icon("CircleX"),
							},
						})}
					>
						<Menu.Button />
					</Menu>
				)}
			</RowActionsWrapper>
			{editVehiclesModal.isOpen && (
				<EditReturnOrderVehiclesModal
					isOpen={editVehiclesModal.isOpen}
					onClose={editVehiclesModal.onClose}
					onComplete={refetch}
					returnOrderId={cell.row.original.id}
				/>
			)}
			{cancelModal.isOpen && (
				<ConfirmModal
					title={t("stock.return_cancel_order")}
					message={t("stock.return_cancel_order_confirm_message")}
					controller={cancelModal}
				/>
			)}
		</Fragment>
	);
}
